import { isMultilineRichHtmlFieldKey } from './multilineRichHtmlFields';

const RESOLVED_ATTACHMENT_SRC = /sitescope-attachment:\/\/(\d+)/g;
const PENDING_ATTACHMENT_SRC = /sitescope-attachment-pending:\/\/([\w-]+)/g;

export function collectRichHtmlAttachmentIds(html: string): number[] {
  const ids = new Set<number>();
  for (const match of html.matchAll(RESOLVED_ATTACHMENT_SRC)) {
    const id = Number.parseInt(match[1], 10);
    if (Number.isInteger(id) && id > 0) {
      ids.add(id);
    }
  }
  // Webpack babel `loose` spread compiles `[...set]` to `[].concat(set)` → `[Set]`.
  return Array.from(ids);
}

export function removeRichHtmlAttachmentImage(
  html: string,
  attachmentId: number,
): string {
  if (!Number.isInteger(attachmentId) || attachmentId <= 0) {
    return html;
  }

  const escapedId = String(attachmentId).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const imagePattern = new RegExp(
    `<img\\b[^>]*\\bsrc=(["'])sitescope-attachment:\\/\\/${escapedId}\\/?\\1[^>]*>`,
    'gi',
  );
  return html.replace(imagePattern, '');
}

function rewriteRichHtmlAttachmentInValue(
  value: unknown,
  key: string,
  attachmentId: number,
): { value: unknown; changed: boolean } {
  if (typeof value === 'string' && isMultilineRichHtmlFieldKey(key)) {
    const nextValue = removeRichHtmlAttachmentImage(value, attachmentId);
    return { value: nextValue, changed: nextValue !== value };
  }

  if (Array.isArray(value)) {
    let changed = false;
    const nextValue = value.map(item => {
      const result = rewriteRichHtmlAttachmentInValue(item, key, attachmentId);
      changed ||= result.changed;
      return result.value;
    });
    return { value: changed ? nextValue : value, changed };
  }

  if (value !== null && typeof value === 'object') {
    let changed = false;
    const source = value as Record<string, unknown>;
    const nextValue: Record<string, unknown> = {};
    for (const [childKey, childValue] of Object.entries(source)) {
      const result = rewriteRichHtmlAttachmentInValue(
        childValue,
        childKey,
        attachmentId,
      );
      nextValue[childKey] = result.value;
      changed ||= result.changed;
    }
    return { value: changed ? nextValue : value, changed };
  }

  return { value, changed: false };
}

/**
 * Builds the smallest top-level edit patch needed after an inline attachment
 * is deleted from an attachment list. Nested repeatable rows stay grouped under
 * their owning request property so existing save bridges persist the change.
 */
export function removeRichHtmlAttachmentFromRequestData(
  requestData: Readonly<Record<string, unknown>>,
  attachmentId: number,
): Record<string, unknown> {
  const patch: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(requestData)) {
    const result = rewriteRichHtmlAttachmentInValue(value, key, attachmentId);
    if (result.changed) {
      patch[key] = result.value;
    }
  }
  return patch;
}

export function collectRequestRichHtmlAttachmentIds(
  requestData: Readonly<Record<string, unknown>>,
): number[] {
  const ids = new Set<number>();

  const collect = (value: unknown, key: string): void => {
    if (typeof value === 'string' && isMultilineRichHtmlFieldKey(key)) {
      collectRichHtmlAttachmentIds(value).forEach(id => ids.add(id));
      return;
    }
    if (Array.isArray(value)) {
      value.forEach(item => collect(item, key));
      return;
    }
    if (value !== null && typeof value === 'object') {
      Object.entries(value as Record<string, unknown>).forEach(
        ([childKey, childValue]) => collect(childValue, childKey),
      );
    }
  };

  Object.entries(requestData).forEach(([key, value]) => collect(value, key));
  return Array.from(ids);
}

export function collectPendingRichHtmlAttachmentIds(html: string): string[] {
  const ids = new Set<string>();
  for (const match of html.matchAll(PENDING_ATTACHMENT_SRC)) {
    if (match[1]) {
      ids.add(match[1]);
    }
  }
  return Array.from(ids);
}

export function rewritePendingRichHtmlAttachmentRefs(
  html: string,
  attachmentIdsByClientId: Readonly<Record<string, number>>,
): string {
  return html.replace(
    PENDING_ATTACHMENT_SRC,
    (fullMatch: string, clientId: string) => {
      const attachmentId = attachmentIdsByClientId[clientId];
      return Number.isInteger(attachmentId) && attachmentId > 0
        ? `sitescope-attachment://${attachmentId}`
        : fullMatch;
    },
  );
}

const UNRESOLVED_ATTACHMENT_IMG =
  /<img\b[^>]*\bsrc=(["'])sitescope-attachment(?:-pending)?:\/\/[^"']+\1[^>]*>/gi;

/**
 * The visible stand-in for an `<img>` tag that cannot be shown: its `alt`
 * when it has one, so a reader knows WHICH picture is missing. One shape for
 * every caller (request attachments here, message images in `messageHtml`).
 */
export function imagePlaceholderForTag(tag: string): string {
  const alt = /\balt=(["'])(.*?)\1/i.exec(tag)?.[2]?.trim();
  return alt ? `<span>[image: ${alt}]</span>` : '<span>[image]</span>';
}

/**
 * Native Image / RenderHTML cannot load `sitescope-attachment://` URIs.
 * Swap them for a text stand-in until a data/http src is ready.
 */
export function placeholderUnresolvedAttachmentImages(html: string): string {
  return html.replace(UNRESOLVED_ATTACHMENT_IMG, imagePlaceholderForTag);
}

export function replaceRichHtmlAttachmentSrc(
  html: string,
  attachmentId: number,
  resolvedSrc: string | null,
): string {
  const escapedId = String(attachmentId).replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const imgPattern = new RegExp(
    `<img\\b([^>]*\\bsrc=(["'])sitescope-attachment:\\/\\/${escapedId}\\/?\\2[^>]*)>`,
    'gi',
  );
  return html.replace(imgPattern, tag => {
    if (!resolvedSrc) {
      return imagePlaceholderForTag(tag);
    }
    return tag.replace(
      /(\bsrc=)(["'])sitescope-attachment:\/\/\d+\/?\2/i,
      `$1"${resolvedSrc}"`,
    );
  });
}
